import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GoogleMap, InfoWindow, useLoadScript } from "@react-google-maps/api"
import { Spinner } from "reactstrap"
import { useTranslation } from "react-i18next"
import {
  defaultLat,
  defaultLng,
  googleAPIKeys,
  GOOGLE_MAPS_LIBRARIES,
} from "common/utils"
import {
  formatOrderAddress,
  getBranchCoords,
  getOrderCustomerName,
  getOrderDeliveryCoords,
  getOrderBranchName,
  spreadOrderMarkerPositions,
} from "utils/mapMarkerUtils"
import {
  clearOrdersMapOverlays,
  createOrdersMapOverlays,
  fitOrdersMapBounds,
  refreshOrdersMapOverlays,
} from "utils/ordersMapMarkerOverlay"
import formatDisplayDateTime from "components/Shared/formatDateTime"
import { getSystemStatusDisplay } from "utils/orderStatusUtils"
const MAP_CONTAINER_STYLE = { width: "100%", height: "560px" }

const OrdersDeliveryMap = ({
  branches = [],
  orders = [],
  loading = false,
  onOpenOrderModal,
  language = "en",
  focusBranchIds = [],
  isActive = true,
  filteredOrderTotal = null,
  maxOrdersOnMap = 500,
  branchCount = null,
  showEstimatedDeliveryDate = false,
}) => {
  const { t } = useTranslation()
  const mapRef = useRef(null)
  const overlaysRef = useRef([])
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [mapReady, setMapReady] = useState(false)

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleAPIKeys,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  const branchMarkers = useMemo(
    () =>
      (branches || [])
        .map(branch => {
          const position = getBranchCoords(branch)
          if (!position) return null
          return {
            id: String(branch._id),
            position,
            name: language === "sa" ? branch.nameAr || branch.name : branch.name,
            imageUrl: branch.imageUrl,
          }
        })
        .filter(Boolean),
    [branches, language]
  )

  const orderMarkers = useMemo(() => {
    const baseMarkers = (orders || [])
      .filter(order => order?.orderType === "delivery")
      .map(order => {
        const position = getOrderDeliveryCoords(order)
        if (!position) return null
        return {
          id: String(order._id),
          order,
          position,
          orderNo: order.invoiceNo,
          customerName: getOrderCustomerName(order, language),
          address: formatOrderAddress(order, language),
          branchName: getOrderBranchName(order, language),
          statusDisplay: getSystemStatusDisplay(order, t),
          riderName: order.deliveryPartnerName,
          deliveryStatus: order.deliveryStatus,
          notes: order.notes,
        }
      })
      .filter(Boolean)

    return spreadOrderMarkerPositions(baseMarkers, branchMarkers)
  }, [orders, language, t, branchMarkers])

  const displayedBranchCount =
    branchCount != null ? branchCount : branchMarkers.length
  const displayedOrderTotal =
    filteredOrderTotal != null ? filteredOrderTotal : orderMarkers.length
  const plottedOrderCount = orderMarkers.length
  const ordersTruncatedOnMap =
    filteredOrderTotal != null &&
    filteredOrderTotal > orders.length &&
    orders.length >= maxOrdersOnMap

  const updateMapViewport = useCallback(() => {
    fitOrdersMapBounds({
      map: mapRef.current,
      branchMarkers,
      orderMarkers,
      focusBranchIds,
    })
  }, [branchMarkers, orderMarkers, focusBranchIds])

  const renderOverlays = useCallback(() => {
    const map = mapRef.current
    if (!map || !window.google?.maps) return

    clearOrdersMapOverlays(overlaysRef.current)
    overlaysRef.current = createOrdersMapOverlays({
      map,
      branchMarkers,
      orderMarkers,
      onBranchMarkerClick: marker => {
        setSelectedOrder(null)
        setSelectedBranch(marker)
      },
      onOrderMarkerClick: marker => {
        setSelectedBranch(null)
        setSelectedOrder(marker)
      },
    })
  }, [branchMarkers, orderMarkers])

  useEffect(() => {
    if (!isLoaded || !mapReady || !isActive) return

    const map = mapRef.current
    if (!map) return

    const syncMap = () => {
      window.google?.maps?.event.trigger(map, "resize")
      renderOverlays()
      updateMapViewport()
      window.google?.maps?.event.addListenerOnce(map, "idle", () => {
        refreshOrdersMapOverlays(overlaysRef.current)
      })
    }

    const frameId = requestAnimationFrame(syncMap)
    return () => {
      cancelAnimationFrame(frameId)
      clearOrdersMapOverlays(overlaysRef.current)
    }
  }, [isLoaded, mapReady, isActive, renderOverlays, updateMapViewport])

  if (loadError) {
    return (
      <div className="text-center py-5 text-danger">
        <i className="bx bx-map-alt font-size-48 d-block mb-2"></i>
        <p>{t("Failed to load map")}</p>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2 text-muted">{t("Loading map...")}</p>
      </div>
    )
  }

  const defaultCenter =
    branchMarkers[0]?.position ||
    orderMarkers[0]?.position || { lat: defaultLat, lng: defaultLng }

  return (
    <div className="position-relative orders-delivery-map">
      {loading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
          style={{ zIndex: 2 }}
        >
          <Spinner color="primary" />
        </div>
      )}

      <div className="d-flex flex-wrap align-items-center gap-3 mb-3 small text-muted">
        <span className="d-inline-flex align-items-center gap-1">
          <i className="bx bxs-store text-primary"></i>
          {t("Branches ({{count}})", { count: displayedBranchCount })}
        </span>
        <span className="d-inline-flex align-items-center gap-1">
          <i className="bx bxs-map text-danger"></i>
          {t("Delivery Orders ({{count}})", { count: displayedOrderTotal })}
          {ordersTruncatedOnMap ? (
            <span className="ms-1">
              {t("Showing {{count}} on map", { count: plottedOrderCount })}
            </span>
          ) : null}
        </span>
      </div>

      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={defaultCenter}
        zoom={10}
        onLoad={map => {
          mapRef.current = map
          setMapReady(true)
        }}
        onUnmount={() => {
          clearOrdersMapOverlays(overlaysRef.current)
          overlaysRef.current = []
          mapRef.current = null
          setMapReady(false)
        }}
        onClick={() => {
          setSelectedBranch(null)
          setSelectedOrder(null)
        }}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {selectedBranch && (
          <InfoWindow
            position={selectedBranch.position}
            onCloseClick={() => setSelectedBranch(null)}
            options={{ maxWidth: 260, headerDisabled: true }}
          >
            <div className="orders-map-infowindow">
              <div className="d-flex align-items-center gap-2 mb-1">
                {selectedBranch.imageUrl ? (
                  <img
                    src={selectedBranch.imageUrl}
                    alt={selectedBranch.name}
                    width={32}
                    height={32}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                  />
                ) : (
                  <i className="bx bxs-store text-primary font-size-20"></i>
                )}
                <strong>{selectedBranch.name}</strong>
              </div>
              <p className="mb-0 small text-muted">{t("Branch Location")}</p>
            </div>
          </InfoWindow>
        )}

        {selectedOrder && (
          <InfoWindow
            position={selectedOrder.position}
            onCloseClick={() => setSelectedOrder(null)}
            options={{ maxWidth: 300, headerDisabled: true }}
          >
            <div className="orders-map-infowindow">
              <strong>
                {t("Order")} #{selectedOrder.orderNo || "-"}
              </strong>
              {selectedOrder.customerName && (
                <p className="mb-1 small">{selectedOrder.customerName}</p>
              )}
              {selectedOrder.branchName && (
                <p className="mb-1 small text-muted">
                  {t("Branch")}: {selectedOrder.branchName}
                </p>
              )}
              {selectedOrder.address && (
                <p className="mb-1 small text-muted">{selectedOrder.address}</p>
              )}
              {selectedOrder.notes ? (
                <p className="mb-1 small text-muted text-break">
                  {t("Notes")}: {selectedOrder.notes}
                </p>
              ) : null}
              <p className="mb-1 small">
                {t("Status")}:{" "}
                <span
                  style={{
                    color: selectedOrder.statusDisplay?.color || "#9B9B9B",
                    fontWeight: 600,
                  }}
                >
                  {selectedOrder.statusDisplay?.text || "-"}
                </span>
              </p>
              {showEstimatedDeliveryDate ? (
                <p className="mb-1 small text-muted">
                  {t("Estimated Delivery Date")}:{" "}
                  {selectedOrder.order?.orderType === "delivery" &&
                  selectedOrder.order?.estimatedDeliveryDate
                    ? formatDisplayDateTime(
                        selectedOrder.order.estimatedDeliveryDate
                      )
                    : "-"}
                </p>
              ) : null}
              <p className="mb-2 small">
                {t("Rider")}: {selectedOrder.riderName || t("Not Assigned")}
              </p>
              <button
                type="button"
                className="orders-map-infowindow__action"
                onClick={event => {
                  event.stopPropagation()
                  onOpenOrderModal?.(selectedOrder.order)
                }}
              >
                {t("View & Assign")}
              </button>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {!loading && branchMarkers.length === 0 && (
        <p className="text-center text-muted mt-3 mb-0">
          {t("No branch locations found for the selected filters.")}
        </p>
      )}
      {!loading && branchMarkers.length > 0 && orderMarkers.length === 0 && (
        <p className="text-center text-muted mt-3 mb-0">
          {t("No delivery orders found for the selected filters.")}
        </p>
      )}
    </div>
  )
}

export default OrdersDeliveryMap
